import type { ReactNode } from 'react';
import { GripVertical } from 'lucide-react';
import { fmtNumber } from '../../lib/format';

type Tone = 'positive' | 'negative' | 'neutral';
export function observedNumber(value: unknown): number | null {
  if (value == null || value === '' || !['number', 'string'].includes(typeof value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}
export function numericTone(value: unknown): Tone {
  const number = observedNumber(value);
  return number == null || number === 0 ? 'neutral' : number > 0 ? 'positive' : 'negative';
}
export function PanelTitle({ children, icon = <GripVertical size={17}/> }: { children: ReactNode; icon?: ReactNode }) {
  return <h3 className="card-title"><span className="panel-icon" aria-hidden="true">{icon}</span>{children}</h3>;
}
export function MetricPanel({ title, subtitle, icon, status, value, children, className = '' }: {
  title: ReactNode; subtitle?: ReactNode; icon?: ReactNode; status?: ReactNode;
  value?: ReactNode; children: ReactNode; className?: string;
}) {
  return <article className={`metric-tile metric-panel ${className}`}>
    <header className="metric-panel-head">
      <div className="metric-panel-title">{icon && <span className="panel-icon" aria-hidden="true">{icon}</span>}<h4>{title}</h4>{subtitle && <span className="metric-panel-subtitle">{subtitle}</span>}</div>
      {value != null && <div className="metric-panel-value">{value}</div>}
      {status && <div className="metric-panel-status">{status}</div>}
    </header>
    {children}
  </article>;
}
export type MetricFact = { label: string; value: ReactNode; tone?: Tone; note?: ReactNode };
export function MetricFacts({ items, columns = 2, className = '' }: { items: MetricFact[]; columns?: 2 | 3; className?: string }) {
  return <dl className={`monitor-facts metric-facts metric-facts-${columns} ${className}`}>
    {items.map((item, index) => <div className="metric-fact" key={`${item.label}:${index}`}>
      <dt>{item.label}</dt><dd className={item.tone ? `value-${item.tone}` : undefined}>{item.value}</dd>
      {item.note && <p className="metric-fact-note">{item.note}</p>}
    </div>)}
  </dl>;
}
export function SignedReadout({ label, value, digits = 3 }: { label: string; value: unknown; digits?: number }) {
  const number = observedNumber(value);
  const inRange = number != null && number >= -1 && number <= 1;
  return <div className="signed-readout" data-observed={number != null}>
    <div className="signed-readout-head"><span>{label}</span><strong className={`value-${numericTone(number)}`}>{fmtNumber(number, { digits, sign: true })}</strong></div>
    <div className="signed-readout-track" aria-hidden="true">
      {inRange && <span className={`signed-readout-fill value-${numericTone(number)}`} style={{ left: `${number >= 0 ? 50 : 50 + number * 50}%`, width: `${Math.abs(number) * 50}%` }}/>}
    </div>
    <p className="metric-fact-note">{number == null ? '미관측 · 0으로 대체하지 않습니다.' : inRange ? '표시 범위 -1~1 · 가운데 0' : '표시 범위 -1~1 밖의 원자료 · 단위 확인 필요'}</p>
  </div>;
}
